// Inspections — list page per the design: header, KPI stat tiles (submission
// counts — placeholder zeros until the backend exposes a counts endpoint),
// Available/Submissions/Archived tabs, a toolbar (debounced search, the
// two-pane Filter, count, Cards/Table toggle, Columns menu), and the rich
// DataTable (server-side pagination via POST /inspections/all — its filter
// params are all supported by the confirmed contract). The Submissions tab
// renders the shared SubmissionsList over EVERY inspection's submissions; the
// Cards view is still a placeholder until its design ships.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ColumnsMenu,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  SegmentedControl,
  TableFooter,
  Tabs,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import {
  InspectionBulkActionType,
  InspectionDueDateFilter,
  InspectionSortKey,
  useBulkActionInspectionsMutation,
  useDeleteInspectionsMutation,
  useGetAccessibleInspectionUsersQuery,
  useGetInspectionDetailQuery,
  useGetInspectionsQuery,
  type InspectionListItem,
  type InspectionPriority,
  type InspectionState,
} from '@/api/endpoints/inspectionApi';
import {
  useCreateInspectionTagMutation,
  useDeleteInspectionTagMutation,
  useGetInspectionTagsQuery,
  useUpdateInspectionTagMutation,
} from '@/api/endpoints/tagsApi';
import { useGetProjectsSelectionQuery } from '@/api/endpoints/projectsApi';
import { useGetFormsSelectionQuery } from '@/api/endpoints/formsApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import { SubmissionSourceType } from '@/api/endpoints/submissionsApi';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useColumnVisibility from '@/hooks/useColumnVisibility';
import useModuleAccess from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import TagsModal, { type TagDraft } from '@/pages/structure-page/TagsModal';
import SubmissionsList from '@/pages/submissions-page/SubmissionsList';
import { SUBMISSION_ICON_COLUMN_KEYS } from '@/pages/submissions-page/submissionColumns';
import type { OrgUnit } from '@/mocks/structure';
import { useInspectionColumns } from './InspectionColumns';
import InspectionCards from './InspectionCards';
import CreateInspection from './CreateInspection';
import EditInspection from './EditInspection';
import { DEV_ONLY } from '@/config/config';

// Flatten the structure tree into a flat unit list (same idiom as PeoplePage).
const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

/** Just the row facts the selection bar's conditions need — not the whole row.
 *  Keeping them per selected id means draft/suspended checks keep working for
 *  rows selected on earlier pages (PeoplePage idiom). */
interface SelectedInspection {
  id: string;
  state: InspectionState;
}
const toSelected = (i: InspectionListItem): SelectedInspection => ({ id: i.id, state: i.state });

// Cards-view page size — divides evenly into the grid's 1/2/3/4/6-per-row
// counts, so every loaded batch lands as whole rows at any width.
const CARDS_PAGE_SIZE = 12;

/** One pending delete, whichever path opened it. `name` is set only when a
 *  single row was targeted, so the confirmation can name it; the selection bar
 *  leaves it undefined and gets the count-worded copy. The endpoint is bulk, so
 *  both paths are the same request (the data-source idiom). */
interface PendingDelete {
  ids: string[];
  name?: string;
}

// Toolbar Filter category keys (rail order) + the empty selection shape
// (init + clear). `nodes` maps to the `companyNode` body field.
const FILTER_KEYS = [
  'state',
  'priority',
  'tags',
  'dueDate',
  'form',
  'createdBy',
  'assignedTo',
  'project',
  'nodes',
] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// Filter option dots — semantic status tokens per value (ProjectsPage idiom).
const STATE_DOT: Record<InspectionState, string> = {
  active: 'var(--brand)',
  draft: 'var(--text-3)',
  suspended: 'var(--warning)',
};
const PRIORITY_DOT: Record<InspectionPriority, string> = {
  high: 'var(--danger)',
  medium: 'var(--warning)',
  low: 'var(--info)',
};

// KPI tiles — submission-state counts. No backend endpoint yet: placeholder
// zeros, wire the values once the counts contract exists.
const STATS = [
  { key: 'awaiting', icon: 'clock', tone: 'info' },
  { key: 'returned', icon: 'corner-up-left', tone: 'warning' },
  { key: 'on_hold', icon: 'pause', tone: 'neutral' },
  { key: 'completed', icon: 'circle-check', tone: 'success' },
] as const;

// Success copy per lifecycle verb. `count` is passed for all four — only the
// archive keys pluralise on it, the rest ignore it (same as inspection_deleted).
const ACTION_TOAST: Record<InspectionBulkActionType, string> = {
  [InspectionBulkActionType.DUPLICATE]: 'inspection_duplicated',
  [InspectionBulkActionType.SUSPEND]: 'inspection_suspended',
  [InspectionBulkActionType.RESUME]: 'inspection_resumed',
  [InspectionBulkActionType.ARCHIVE]: 'inspection_archived_toast',
};

// Suspend and Archive take an inspection OUT of active service, so they confirm
// first (the projects archive idiom). Duplicate and Resume are additive /
// restorative and fire straight away — a confirm there is just noise.
const CONFIRM_ACTIONS: InspectionBulkActionType[] = [
  InspectionBulkActionType.SUSPEND,
  InspectionBulkActionType.ARCHIVE,
];

/** Router state the project detail page's Inspections tab hands over when its
 *  "New inspection" button is pressed: open the create drawer with this project
 *  PRESELECTED — the select stays editable, nothing is locked (by decision).
 *  Every key is optional and a stateless visit is a
 *  NO-OP — unlike /submissions or /datasource/entries, which bail with a
 *  <Navigate> when their state is missing, /inspections is a top-level list
 *  route that must work perfectly well with no state at all. Don't "fix" this
 *  into a Navigate guard. */
interface InspectionsRouteState {
  createProjectId?: string;
}

// The Submissions tab is module-level, not per-inspection: an EMPTY recordIds
// with type 'inspection' asks the endpoint for every accessible inspection's
// submissions. Empty by design — not a missing id.
const EMPTY_RECORD_IDS: string[] = [];

const InspectionPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Verb gates (hidden, not disabled, when missing). Duplicate creates a new
  // inspection so it rides `create`; Suspend/Resume/Archive and the Tags
  // library ride `edit` (no inspections extras for either); Delete needs its
  // own verb AND the Archived tab (archive-then-delete rule). Row click opens
  // the EDIT drawer — the module has no read surface — so it too needs `edit`.
  const { canCreate, canEdit, canDelete } = useModuleAccess('inspections');
  const [tab, setTab] = useState<'available' | 'submissions' | 'archived'>('available');
  const [view, setView] = useState<'cards' | 'table'>('table');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  // The sortable columns are keyed by InspectionSortKey itself, so this state IS
  // the request's orderBy — no mapping layer. Starts undefined: nothing is sent
  // (backend natural order) until a header is clicked. One state serves both
  // tabs — same endpoint, only `status` differs.
  const [sort, setSort] = useState<{ key: InspectionSortKey; type: 'asc' | 'desc' }>();
  // Cards view paginates by load-more: its own page counter plus a pages-map
  // (page number → that page's items) accumulated across fetches. Keying by
  // page means a LIST-invalidation refetch (edit save, bulk action) REPLACES
  // its page's slot instead of appending duplicates. Known v1 caveat: an edit
  // to an item on an EARLIER accumulated page stays stale until the stack
  // resets (tab/search/filter/view change).
  const [cardsPage, setCardsPage] = useState(1);
  const [cardPages, setCardPages] = useState<Map<number, InspectionListItem[]>>(new Map());
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Due-date FROM/TO (keyed by category, i.e. `dueDate`) — applied on Done
  // together with `filters`.
  const [dueRanges, setDueRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  const [selected, setSelected] = useState<Map<string, SelectedInspection>>(new Map());
  const [createOpen, setCreateOpen] = useState(false);
  // Set only by the router-state hand-off below; seeds the drawer's project
  // select. Cleared on close so a later manual "New inspection" starts from the
  // public-project default again rather than inheriting this project.
  const [defaultProjectId, setDefaultProjectId] = useState<string | undefined>();
  // Row click / kebab Edit opens the edit drawer for this inspection.
  const [editId, setEditId] = useState<string | null>(null);
  // One delete dialog for both paths — the row kebab passes { ids: [row.id],
  // name } and the selection bar { ids: selection }. No hand-rolled in-flight
  // flag: the mutation's own isLoading covers it.
  const [confirmDelete, setConfirmDelete] = useState<PendingDelete | null>(null);
  // Pending Suspend / Archive — the row kebab ({ ids: [row.id] }) and the
  // selection bar ({ ids: selection }) share one confirm dialog and one call.
  const [confirmAction, setConfirmAction] = useState<{
    ids: string[];
    action: InspectionBulkActionType;
  } | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);

  // Edit drawer data — the full inspection (list rows lack location/coords).
  const { data: editDetail, isFetching: editDetailLoading } = useGetInspectionDetailQuery(
    editId ?? '',
    { skip: !editId },
  );

  // Inspection tag library — the TagsModal manages it; the Filter reuses it.
  const { data: inspectionTags = [] } = useGetInspectionTagsQuery();
  const [createInspectionTag] = useCreateInspectionTagMutation();
  const [updateInspectionTag] = useUpdateInspectionTagMutation();
  const [deleteInspectionTag] = useDeleteInspectionTagMutation();
  const [deleteInspections, { isLoading: deleting }] = useDeleteInspectionsMutation();
  const [bulkAction, { isLoading: acting }] = useBulkActionInspectionsMutation();

  // Filter option data — projects, form templates, the structure tree and the
  // two people lists (company users for Created by, inspection-accessible
  // users for Assigned to).
  const { data: projects = [] } = useGetProjectsSelectionQuery();
  const { data: forms = [] } = useGetFormsSelectionQuery();
  const { data: structure = [] } = useGetStructureSelectionQuery();
  const { data: companyUsers = [] } = useGetUsersSelectionQuery();
  const { data: inspectionUsers = [] } = useGetAccessibleInspectionUsersQuery();

  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'state',
        label: t('inspection_col_state'),
        icon: 'circle-dot',
        options: (['active', 'draft', 'suspended'] as const).map((s) => ({
          id: s,
          label: t(`inspection_state_${s}`),
          dotColor: STATE_DOT[s],
        })),
      },
      {
        key: 'priority',
        label: t('inspection_col_priority'),
        icon: 'flag',
        options: (['low', 'medium', 'high'] as const).map((p) => ({
          id: p,
          label: t(`inspection_priority_${p}`),
          dotColor: PRIORITY_DOT[p],
        })),
      },
      {
        key: 'tags',
        label: t('inspection_col_tags'),
        icon: 'tag',
        searchable: true,
        searchPlaceholder: t('tags_picker_search'),
        options: inspectionTags.map((tag) => ({ id: tag.id, label: tag.title })),
      },
      {
        key: 'dueDate',
        label: t('text_due_date'),
        icon: 'calendar',
        type: 'single' as const,
        rangeOptionId: InspectionDueDateFilter.CUSTOM,
        options: [
          {
            id: InspectionDueDateFilter.ANY_DATE,
            label: t('inspection_filter_due_any'),
            icon: 'clock',
          },
          {
            id: InspectionDueDateFilter.NO_DATE,
            label: t('inspection_filter_due_none'),
            icon: 'calendar-off',
          },
          {
            id: InspectionDueDateFilter.CUSTOM,
            label: t('inspection_filter_due_custom'),
            icon: 'calendar',
          },
        ],
      },
      {
        key: 'form',
        label: t('inspection_col_form'),
        icon: 'file-text',
        searchable: true,
        searchPlaceholder: t('forms_search_ph'),
        options: forms.map((f) => ({ id: f.id, label: f.name })),
      },
      {
        key: 'createdBy',
        label: t('inspection_col_created_by'),
        icon: 'user',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: companyUsers.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'assignedTo',
        label: t('inspection_field_assigned'),
        icon: 'user-check',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: inspectionUsers.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'project',
        label: t('inspection_col_project'),
        icon: 'folder-kanban',
        searchable: true,
        searchPlaceholder: t('inspection_filter_project_ph'),
        options: projects.map((p) => ({ id: p.id, label: p.name })),
      },
      {
        key: 'nodes',
        label: t('inspection_col_structure'),
        icon: 'network',
        searchable: true,
        searchPlaceholder: t('people_bulk_node_search_ph'),
        options: flattenUnits(structure)
          .filter((u) => u.level !== 0)
          .map((u) => ({ id: u.id, label: u.name })),
      },
    ],
    [t, inspectionTags, forms, projects, structure, companyUsers, inspectionUsers],
  );

  const ids = (s: Set<string>) => (s.size ? [...s] : undefined);
  // Single-select: 0..1-sized set; sent only when the user actively picked one.
  const dueDate = filters.dueDate.size
    ? ([...filters.dueDate][0] as InspectionDueDateFilter)
    : undefined;

  const archivedTab = tab === 'archived';
  const cardsView = view === 'cards';
  // ONE request at a time — always for the visible tab; Submissions has no
  // list endpoint yet, so the query skips there. Both views share this query:
  // the view picks whose page/limit drive it (table pages, cards load-more).
  const queryPage = cardsView ? cardsPage : page;
  const { data, currentData, isLoading, isFetching, isError } = useGetInspectionsQuery(
    {
      page: queryPage,
      limit: cardsView ? CARDS_PAGE_SIZE : limit,
      status: archivedTab ? 'archived' : 'active',
      search: debouncedSearch || undefined,
      priority: ids(filters.priority) as InspectionPriority[] | undefined,
      state: ids(filters.state) as InspectionState[] | undefined,
      tags: ids(filters.tags),
      form: ids(filters.form),
      project: ids(filters.project),
      companyNode: ids(filters.nodes),
      createdBy: ids(filters.createdBy),
      assignedTo: ids(filters.assignedTo),
      inspectionDueDate: dueDate,
      inspectionDueDateStart:
        dueDate === InspectionDueDateFilter.CUSTOM
          ? dueRanges.dueDate?.start || undefined
          : undefined,
      inspectionDueDateEnd:
        dueDate === InspectionDueDateFilter.CUSTOM
          ? dueRanges.dueDate?.end || undefined
          : undefined,
      orderBy: sort,
    },
    { refetchOnMountOrArgChange: true, skip: tab === 'submissions' },
  );
  // A new search term, filter, or sort reshapes the result set — back to page 1.
  useEffect(() => setPage(1), [debouncedSearch, filters, dueRanges, sort]);

  // Cards: the result set changed shape or the view toggled — collapse the
  // accumulated stack back to its first page.
  useEffect(() => {
    setCardsPage(1);
    setCardPages(new Map());
  }, [tab, debouncedSearch, filters, dueRanges, sort, view]);

  // Record each fulfilled cards page under its number (a refetch of the same
  // args replaces the slot — see the pages-map comment at the state).
  useEffect(() => {
    if (cardsView && currentData)
      setCardPages((prev) => new Map(prev).set(cardsPage, currentData.items));
  }, [cardsView, currentData, cardsPage]);

  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, queryPage);

  // Display-only memory of each tab's last-seen total (badges; no extra calls).
  // Recorded only while search + filters are empty so badges stay true totals
  // (ProjectsPage idiom).
  const filtersActive =
    !!debouncedSearch.trim() || FILTER_KEYS.some((key) => filters[key].size > 0);
  const [lastTotals, setLastTotals] = useState<{ available?: number; archived?: number }>({});
  useEffect(() => {
    if (data && tab !== 'submissions' && !filtersActive)
      setLastTotals((c) => ({ ...c, [archivedTab ? 'archived' : 'available']: total }));
  }, [data, total, archivedTab, tab, filtersActive]);

  // Consume the "create for this project" hand-off exactly once, then STRIP it
  // from history: location.state lives in window.history.state and survives a
  // reload, so leaving it in place would silently re-open the drawer on every
  // refresh of /inspections.
  const handoffDone = useRef(false);
  useEffect(() => {
    if (handoffDone.current) return;
    const { createProjectId } = (location.state as InspectionsRouteState | null) ?? {};
    if (!createProjectId) return;
    handoffDone.current = true;
    setDefaultProjectId(createProjectId);
    setCreateOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const closeCreate = () => {
    setCreateOpen(false);
    setDefaultProjectId(undefined);
  };

  // Duplicate / suspend / resume / archive — ONE call (POST
  // /inspections/bulk-action) over a pending id set, so the row kebab and the
  // selection bar share the same mutation. The LIST invalidation refetches the
  // visible tab, so an archived row drops out of it on its own.
  const runAction = async (ids: string[], action: InspectionBulkActionType): Promise<boolean> => {
    try {
      await bulkAction({ inspectionIds: ids, action }).unwrap();
      toast.success(t(ACTION_TOAST[action], { count: ids.length }));
      // Prune the acted-on rows from the selection. Not just tidiness: the Map
      // CACHES each row's `state`, and suspend/resume just rewrote it — keeping
      // the entry would leave the bar deciding Suspend-vs-Resume on stale facts.
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      return true;
    } catch {
      toast.error(t('error_generic'));
      return false;
    }
  };

  // Single entry point for all four verbs: confirm the two that need it, fire
  // the rest. Both call sites hand over an id array (one element for the
  // single-select verbs), which is exactly what the endpoint takes.
  const startAction = (ids: string[], action: InspectionBulkActionType) => {
    if (!ids.length) return;
    if (CONFIRM_ACTIONS.includes(action)) setConfirmAction({ ids, action });
    else void runAction(ids, action);
  };

  // Same key toggles direction; a new key starts asc. DataTable types onSort as
  // (key: string), but only sortable columns can fire it and each is keyed by an
  // InspectionSortKey — hence the cast at the boundary.
  const handleSort = (raw: string) => {
    const key = raw as InspectionSortKey;
    setSort((prev) =>
      prev?.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'asc' },
    );
  };

  const columns = useInspectionColumns({
    archivedTab,
    sortable: true,
    onEdit: canEdit ? (i) => setEditId(i.id) : undefined,
    // The lifecycle verbs are Available-tab only — the row actions hide them on
    // Archived anyway (no `restore` verb exists), but don't hand them over there.
    onDuplicate: canCreate
      ? (i) => startAction([i.id], InspectionBulkActionType.DUPLICATE)
      : undefined,
    onSuspendResume: canEdit
      ? (i) =>
          startAction(
            [i.id],
            i.state === 'suspended'
              ? InspectionBulkActionType.RESUME
              : InspectionBulkActionType.SUSPEND,
          )
      : undefined,
    onArchive: canEdit ? (i) => startAction([i.id], InspectionBulkActionType.ARCHIVE) : undefined,
    // Delete rides the archive-then-delete rule: a live inspection can only be
    // archived, and only an archived one can be deleted. Archived tab only.
    onDelete:
      archivedTab && canDelete
        ? (i) => setConfirmDelete({ ids: [i.id], name: i.title })
        : undefined,
  });
  const { visibleColumns, menuProps } = useColumnVisibility(columns, {
    locked: ['actions'],
  });

  const items = data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Cards: flatten the pages-map in page order with an id-dedupe (items can
  // shift pages between fetches when records are archived/created mid-stack).
  const cardItems = useMemo(() => {
    const seen = new Set<string>();
    const out: InspectionListItem[] = [];
    [...cardPages.keys()]
      .sort((a, b) => a - b)
      .forEach((p) =>
        cardPages.get(p)!.forEach((i) => {
          if (!seen.has(i.id)) {
            seen.add(i.id);
            out.push(i);
          }
        }),
      );
    return out;
  }, [cardPages]);
  // No button on an empty grid (its own status is showing) nor past the end.
  const cardsHasMore = cardItems.length > 0 && cardItems.length < total;
  // Keep a filled grid visible through invalidation refetches — only an
  // empty-grid fetch shows the status slot.
  const cardsLoading = cardItems.length === 0 && (isLoading || isFetching);
  const cardsLoadingMore = isFetching && cardsPage > 1;

  const switchTab = (value: string) => {
    setTab(value as 'available' | 'submissions' | 'archived');
    setPage(1);
    setSelected(new Map());
    setEditId(null);
  };

  // DataTable wants a Set of ids; the Map keeps the per-row facts alongside.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);

  // DataTable hands back the row id; capture the row's facts from the loaded
  // page (the checkbox can only be clicked while its row is loaded).
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((i) => i.id === id);
        if (row) next.set(id, toSelected(row));
      }
      return next;
    });

  // Header select-all works on the visible page; selection persists across
  // pages (the bar's count reflects everything selected so far).
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));
  const someSelected = !allSelected && items.some((i) => selected.has(i.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((i) => next.delete(i.id));
      else items.forEach((i) => next.set(i.id, toSelected(i)));
      return next;
    });

  // Delete — ONE call to POST /inspections/bulk-delete for a single row or a
  // whole selection, so there is no fan-out and no partial-failure ambiguity.
  const runDelete = async () => {
    if (!confirmDelete) return;
    const { ids, name } = confirmDelete;
    try {
      await deleteInspections({ inspectionIds: ids }).unwrap();
      toast.success(
        name
          ? t('inspection_deleted', { name })
          : t('inspection_bulk_deleted', { count: ids.length }),
      );
      // Drop the deleted rows from the selection — they no longer exist.
      setSelected((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setConfirmDelete(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Diff the modal draft against the fetched inspection tags and fire the
  // matching create / rename / delete mutations; cache invalidation refetches.
  const saveTags = async (draft: TagDraft[]) => {
    const byId = new Map(inspectionTags.map((tag) => [tag.id, tag.title]));
    const draftIds = new Set(draft.filter((d) => d.id).map((d) => d.id));
    const creates = draft.filter((d) => !d.id);
    const updates = draft.filter((d) => d.id && byId.get(d.id) !== d.title);
    const deletes = inspectionTags.filter((tag) => !draftIds.has(tag.id));

    setSavingTags(true);
    try {
      await Promise.all([
        ...creates.map((d) => createInspectionTag({ title: d.title }).unwrap()),
        ...updates.map((d) => updateInspectionTag({ id: d.id as string, title: d.title }).unwrap()),
        ...deletes.map((tag) => deleteInspectionTag(tag.id).unwrap()),
      ]);
      setTagsOpen(false);
      toast.success(t('inspection_tags_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <div className='inspection-page'>
      <header className='inspection-page__header'>
        <div>
          <h1 className='inspection-page__title'>{t('inspection_title')}</h1>
          <p className='inspection-page__subtitle'>{t('inspection_subtitle')}</p>
        </div>
        <div className='inspection-page__actions'>
          {canEdit && (
            <Button variant='secondary' iconStart='tag' onClick={() => setTagsOpen(true)}>
              {t('inspection_action_tags')}
            </Button>
          )}
          {canCreate && (
            <Button iconStart='plus' onClick={() => setCreateOpen(true)}>
              {t('inspection_new')}
            </Button>
          )}
        </div>
      </header>

      <div className='inspection-page__stats'>
        {STATS.map((stat) => (
          <div key={stat.key} className='inspection-page__stat'>
            <span className={`inspection-page__stat-tile inspection-page__stat-tile--${stat.tone}`}>
              <Icon name={stat.icon} size={20} />
            </span>
            <span className='inspection-page__stat-label'>
              <b className='inspection-page__stat-value'>0</b>
              {t(`inspection_stat_${stat.key}`)}
            </span>
          </div>
        ))}
      </div>

      <Tabs
        aria-label={t('inspection_title')}
        value={tab}
        onChange={switchTab}
        items={[
          {
            value: 'available',
            label: t('inspection_tab_available'),
            icon: 'layout-grid',
            count: lastTotals.available,
          },
          {
            value: 'submissions',
            label: t('inspection_tab_submissions'),
            icon: 'file-text',
          },
          {
            value: 'archived',
            label: t('inspection_tab_archived'),
            icon: 'archive',
            count: lastTotals.archived,
          },
        ]}
      />

      {tab !== 'submissions' && (
        <div className='inspection-page__toolbar'>
          <div className='inspection-page__toolbar-lead'>
            <div className='inspection-page__search'>
              <Input
                size='sm'
                iconStart='search'
                placeholder={t('inspection_search_ph')}
                aria-label={t('field_search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <IconButton
                  className='inspection-page__search-clear'
                  icon='x'
                  variant='ghost'
                  size='sm'
                  aria-label={t('people_search_clear')}
                  onClick={() => setSearch('')}
                />
              )}
            </div>
            <FilterDropdown
              label={t('action_filter')}
              categories={filterCategories}
              value={filters}
              ranges={dueRanges}
              onApply={(next, ranges) => {
                setFilters(next);
                setDueRanges(ranges);
              }}
              emptyText={t('text_no_results_found')}
            />
          </div>
          <div className='inspection-page__toolbar-actions'>
            <span className='inspection-page__count'>
              {t('inspection_count', { count: total })}
            </span>
            {DEV_ONLY && (
              <>
                <span className='inspection-page__view-label'>{t('inspection_view_label')}</span>
                <SegmentedControl
                  aria-label={t('inspection_view_label')}
                  value={view}
                  onChange={(v) => {
                    setView(v as 'cards' | 'table');
                    // Selection is table-only (cards have none) — drop it so a
                    // stale selection can't act after cards-side edits.
                    setSelected(new Map());
                  }}
                  // Icon-only segments (no `label`) — ariaLabel names each pill.
                  options={[
                    { value: 'cards', icon: 'layout-grid', ariaLabel: t('inspection_view_cards') },
                    { value: 'table', icon: 'table-2', ariaLabel: t('inspection_view_table') },
                  ]}
                />
              </>
            )}
            {view === 'table' && <ColumnsMenu {...menuProps} />}
          </div>
        </div>
      )}

      {tab === 'submissions' ? (
        // The shared list brings its own toolbar, table and footer — which is
        // why the page toolbar above is suppressed on this tab. The source pills
        // are hidden (every row here is already inspection-sourced) and so is
        // Filter. The REPORT / COMMENTS / LOGS columns are dropped too: this tab
        // passes no onOpenReport and the other two are coming-soon, so all three
        // would be buttons that only toast.
        <SubmissionsList
          type={SubmissionSourceType.Inspection}
          recordIds={EMPTY_RECORD_IDS}
          showSourceFilter={false}
          showFilterButton={false}
          hiddenColumns={SUBMISSION_ICON_COLUMN_KEYS}
          searchPlaceholder={t('inspection_submissions_search_ph')}
          emptyText={t('inspection_submissions_empty')}
        />
      ) : view === 'cards' ? (
        <InspectionCards
          items={cardItems}
          loading={cardsLoading}
          error={isError}
          hasMore={cardsHasMore}
          loadingMore={cardsLoadingMore}
          onLoadMore={() => setCardsPage((p) => p + 1)}
          archivedTab={archivedTab}
          onEdit={canEdit ? (i) => setEditId(i.id) : undefined}
        />
      ) : (
        <Card padded={false} className='inspection-page__panel'>
          {selected.size > 0 && (
            <div className='inspection-page__selection-bar'>
              <div className='inspection-page__selection-lead'>
                <span className='inspection-page__selected'>
                  {t('inspection_selected_count', { count: selected.size })}
                </span>
                {/* The divider needs something after it: Available offers the
                    lifecycle verbs (create/edit), Archived offers Delete. */}
                {(archivedTab ? canDelete : canCreate || canEdit) && (
                  <span className='inspection-page__selection-divider' />
                )}
                {/* Duplicate/Suspend/Resume act on ONE inspection; Archive on
                    many — the endpoint takes an id array either way. All four
                    are AVAILABLE-tab only: there is no `restore` verb, so none
                    of them means anything on Archived. A suspended selection
                    offers Resume instead of Suspend, and a selection containing
                    drafts gets none of them. The Map's per-row facts make these
                    checks work for rows picked on earlier pages. */}
                {!archivedTab &&
                  (() => {
                    const rows = [...selected.values()];
                    if (rows.some((r) => r.state === 'draft')) return null;
                    const suspended = selected.size === 1 && rows[0].state === 'suspended';
                    const ids = [...selected.keys()];
                    return (
                      <>
                        {selected.size === 1 && (
                          <>
                            {canCreate && (
                              <Button
                                variant='ghost'
                                iconStart='copy'
                                disabled={acting}
                                onClick={() => startAction(ids, InspectionBulkActionType.DUPLICATE)}
                              >
                                {t('inspection_action_duplicate')}
                              </Button>
                            )}
                            {canEdit && (
                              <Button
                                variant='ghost'
                                iconStart={suspended ? 'play' : 'pause'}
                                disabled={acting}
                                onClick={() =>
                                  startAction(
                                    ids,
                                    suspended
                                      ? InspectionBulkActionType.RESUME
                                      : InspectionBulkActionType.SUSPEND,
                                  )
                                }
                              >
                                {t(
                                  suspended
                                    ? 'inspection_action_resume'
                                    : 'inspection_action_suspend',
                                )}
                              </Button>
                            )}
                          </>
                        )}
                        {canEdit && (
                          <Button
                            variant='ghost'
                            iconStart='archive'
                            disabled={acting}
                            onClick={() => startAction(ids, InspectionBulkActionType.ARCHIVE)}
                          >
                            {t('inspection_action_archive')}
                          </Button>
                        )}
                      </>
                    );
                  })()}
                {/* Archived tab only, same archive-then-delete rule as the kebab. */}
                {archivedTab && canDelete && (
                  <Button
                    variant='ghost'
                    iconStart='trash-2'
                    className='inspection-page__selection-delete'
                    onClick={() => setConfirmDelete({ ids: [...selected.keys()] })}
                  >
                    {t('button_delete')}
                  </Button>
                )}
              </div>
              <Button variant='ghost' onClick={() => setSelected(new Map())}>
                {t('button_clear')}
              </Button>
            </div>
          )}
          <DataTable
            columns={visibleColumns}
            rows={items}
            rowKey={(i) => i.id}
            minWidth={1560}
            selectable
            selectedIds={selectedIdSet}
            onToggleSelect={toggleSelect}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={toggleSelectAll}
            selectAllLabel={t('inspection_select_all')}
            sort={sort}
            onSort={handleSort}
            onRowClick={canEdit ? (i) => setEditId(i.id) : undefined}
            loading={isLoading}
            error={isError}
            loadingText={t('text_loading')}
            errorText={t('error_generic')}
            empty={
              <>
                <Icon name={archivedTab ? 'archive' : 'clipboard-check'} size={26} />
                {t('inspection_empty')}
              </>
            }
          />

          <TableFooter
            page={page}
            limit={limit}
            total={total}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n);
              setPage(1);
            }}
            perPageLabel={t('inspection_items_per_page')}
            rangeText={t('inspection_range', { from, to, total })}
          />
        </Card>
      )}

      <CreateInspection
        open={createOpen}
        onClose={closeCreate}
        defaultProjectId={defaultProjectId}
      />

      {/* Row-click / kebab edit — the page owns the detail fetch (forms idiom). */}
      <EditInspection
        open={!!editId}
        detail={editId ? (editDetail ?? null) : null}
        loading={editDetailLoading}
        onClose={() => setEditId(null)}
      />

      {/* One dialog for both confirming verbs — the row kebab and the selection
          bar reach it through the same `confirmAction` state, so the copy is
          count-aware and names no inspection (it can be archiving a whole
          selection). Stays open on failure so the error toast has context. */}
      {confirmAction && (
        <ConfirmModal
          open
          onClose={() => setConfirmAction(null)}
          onConfirm={async () => {
            if (await runAction(confirmAction.ids, confirmAction.action)) setConfirmAction(null);
          }}
          tone='warning'
          icon={confirmAction.action === InspectionBulkActionType.ARCHIVE ? 'archive' : 'pause'}
          title={t(
            confirmAction.action === InspectionBulkActionType.ARCHIVE
              ? 'inspection_archive_confirm_title'
              : 'inspection_suspend_confirm_title',
            { count: confirmAction.ids.length },
          )}
          description={t(
            confirmAction.action === InspectionBulkActionType.ARCHIVE
              ? 'inspection_archive_confirm_body'
              : 'inspection_suspend_confirm_body',
            { count: confirmAction.ids.length },
          )}
          confirmLabel={t(
            confirmAction.action === InspectionBulkActionType.ARCHIVE
              ? 'inspection_action_archive'
              : 'inspection_action_suspend',
          )}
          loading={acting}
        />
      )}

      {/* One dialog for both paths — the endpoint is bulk, so a single row and a
          whole selection are the same call; only the copy differs, keyed on
          whether a name was handed over. */}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={
            confirmDelete.name
              ? t('inspection_delete_confirm_title', { name: confirmDelete.name })
              : t('inspection_bulk_delete_title', { count: confirmDelete.ids.length })
          }
          description={t(
            confirmDelete.name ? 'inspection_delete_confirm_body' : 'inspection_bulk_delete_body',
          )}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}

      <TagsModal
        open={tagsOpen}
        tags={inspectionTags}
        saving={savingTags}
        onClose={() => setTagsOpen(false)}
        onSave={saveTags}
        title={t('inspection_tags_title')}
        note={t('inspection_tags_note')}
        emptyText={t('inspection_tags_empty')}
      />
    </div>
  );
};

export default InspectionPage;
